import i18n from '@/i18n'
import { useAccountStore } from '@/stores/account'
import type { GA4Event } from '@/typings/base'
import { MemberCode } from '@/typings/subscribe'
import analytics from './ga4'
import { hashString } from './common'

const toBase64 = (str: string): string => {
  const encoder = new TextEncoder()
  const data = encoder.encode(str)
  let binaryString = ''
  for (const byte of data) {
    binaryString += String.fromCharCode(byte)
  }
  return btoa(binaryString)
}

const fromBase64 = (base64: string): string => {
  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  const decoder = new TextDecoder()
  return decoder.decode(bytes)
}

const cachedEvents = (): Record<string, Event> => {
  const cache = localStorage.getItem('lk_events')
  if (!cache) {
    return {}
  }
  let events: Record<string, Event> = {}
  try {
    events = JSON.parse(fromBase64(cache))
  } catch (e) {
    console.log(e)
  }
  return events || {}
}

const MAX_EVENTS = 100 // 限制最大事件数量

const cacheEvents = (events: Record<string, Event>): void => {
  try {
    const eventsToStore = { ...events }
    const keys = Object.keys(eventsToStore)

    // 如果事件太多，只保留最新的
    if (keys.length > MAX_EVENTS) {
      // 按时间排序（最新的保留）
      const sortedKeys = keys.sort((a, b) => {
        return eventsToStore[b].getStartTime() - eventsToStore[a].getStartTime()
      })

      // 删除旧事件
      const keysToRemove = sortedKeys.slice(MAX_EVENTS)
      keysToRemove.forEach((key) => {
        delete eventsToStore[key]
      })
    }

    // 尝试存储
    localStorage.setItem('lk_events', toBase64(JSON.stringify(eventsToStore)))
  } catch (error) {
    console.warn('事件存储失败，执行紧急清理', error)

    // 紧急情况下，仅保留10个最近的事件
    try {
      const minimalEvents: Record<string, Event> = {}
      const recentKeys = Object.keys(events)
        .sort((a, b) => events[b].getStartTime() - events[a].getStartTime())
        .slice(0, 10)

      recentKeys.forEach((key) => {
        minimalEvents[key] = events[key]
      })

      localStorage.setItem('lk_events', toBase64(JSON.stringify(minimalEvents)))
    } catch (e) {
      // 如果还是失败，彻底清空
      localStorage.removeItem('lk_events')
    }
  }
}

const clearEventCaches = () => {
  const events = cachedEvents()
  const now = Date.now()
  let count = 0

  Object.keys(events).forEach((key) => {
    const e = new Event('').copy(events[key])
    // 删除超过2小时的事件
    if (e.duration(now) > 1000 * 60 * 60 * 2) {
      delete events[key]
      count++
    }
  })

  if (count > 0) {
    cacheEvents(events)
    console.info(`已清理 ${count} 个过期事件`)
  }
}

interface EventOptions {
  time?: {
    start?: number
    end?: number
  }
}

export class Event {
  private _name: GA4Event | string
  private _startTime: number
  private _endTime: number
  private _params: any
  private _userProperties: any
  private _options?: EventOptions

  constructor(name: GA4Event | string, params?: {}, userProperties?: {}, options?: EventOptions) {
    this._name = name
    this._params = params || {}
    this._userProperties = userProperties || {}
    this._startTime = Date.now()
    this._endTime = Date.now()
    this._options = options
    if (options?.time) {
      this._startTime = options.time.start ?? this._startTime
      this._endTime = options.time.end ?? this._endTime
    }
  }

  copy(event: Event) {
    this._name = event._name
    this._params = event._params
    this._userProperties = event._userProperties
    this._startTime = event._startTime
    this._endTime = event._endTime
    this._options = event._options
    return this
  }

  key(key?: string) {
    if (!key) {
      return this
    }
    const events = cachedEvents()
    events[key] = this
    cacheEvents(events)
    console.info(`创建ga4事件记录：${key}`)
    return this
  }

  duration(end?: number) {
    if (end) {
      return end - this._startTime
    }
    return this._endTime - this._startTime
  }

  finish(params?: {}) {
    this.addParams(params || {})
    this._endTime = Date.now()
    return this
  }

  fire() {
    analytics.logEvent(
      this._name,
      {
        event_name: this._name,
        duration: this.duration(),
        ...this._params
      },
      this._userProperties
    )
  }

  addParams(params: {}) {
    this._params = {
      ...this._params,
      ...params
    }
    return this
  }

  getStartTime(): number {
    return this._startTime
  }
}

export class Metrics {
  private _name: string
  private _userProperties: any
  private _options: {}

  constructor(name: string, options?: {}) {
    this._name = name
    this._options = options || {}
    clearEventCaches()
  }

  setUserId(userId: string) {
    analytics.setUserId(userId)
  }

  setClientId(clientId: string) {
    analytics.setClientId(clientId)
  }

  userProperties() {
    const accountStore = useAccountStore()

    const fbCookie = document.cookie.split('; ').find((row) => row.startsWith('_fbc='))
    const fbpCookie = document.cookie.split('; ').find((row) => row.startsWith('_fbp='))
    const gclCookie = document.cookie.split('; ').find((row) => row.startsWith('_gcl_aw='))
    let accountInfo: any = {
      // user_id: accountStore.userId,
      nickname: accountStore.nickname,
      language: i18n.global.locale.value,
      // 哈希处理
      email: accountStore.userEmail ? hashString(accountStore.userEmail) : '',
      fbc: fbCookie ? fbCookie.split('=')[1] : '',
      fbp: fbpCookie ? fbpCookie.split('=')[1] : '',
      gcl: gclCookie ? gclCookie.split('=')[1] : ''
    }
    if (accountStore.memberInfo?.userMember) {
      accountInfo = { ...accountInfo, membership: accountStore.memberInfo.userMember.member.code }
    } else {
      accountInfo = { ...accountInfo, membership: MemberCode.nonMember }
    }
    this._userProperties = accountInfo
    analytics.initUserInfo(accountInfo)
  }

  event(name: GA4Event, params?: {}) {
    return new Event(name, { ...params }, this._userProperties)
  }

  eventOfKey(key: string) {
    const events = cachedEvents()
    const event = events[key]
    if (event) {
      delete events[key]
      cacheEvents(events)
      console.info(`读取ga4事件记录：${key}`)
      return new Event('').copy(event)
    }
  }

  configConsent() {
    analytics.consentConfig()
    return 'denied'
  }

  consentGranted() {
    analytics.consentGranted()
    return 'granted'
  }
}

const metrics = new Metrics('default')
export default metrics
